"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import Footer from "@/components/shared/Footer";

/* ------------------------------------------------------------------ */
/*  Script data                                                        */
/* ------------------------------------------------------------------ */

interface Script {
  id: string;
  title: string;
  description: string;
  icon: string;
  body: string;
}

const SCRIPTS: Script[] = [
  {
    id: "new-patient-welcome",
    title: "New Patient Welcome",
    description:
      "First-impression script for incoming calls from prospective new patients.",
    icon: "1",
    body: `Receptionist: "Thank you for calling [Practice Name], this is [Your Name]. How may I help you today?"

Caller: "Hi, I'd like to make an appointment."

Receptionist: "Of course! Are you a new patient with us, or have you visited before?"

Caller: "I'm a new patient."

Receptionist: "Wonderful, welcome! We love meeting new patients. Let me tell you about our new-patient special — your first exam and X-rays are just $99. We'll do a thorough evaluation so you can meet the doctor and see our office."

Receptionist: "May I get your full name, date of birth, and the best phone number to reach you?"

Caller: [Provides info]

Receptionist: "Perfect. Do you have dental insurance you'd like us to file with?"

Caller: [Responds]

Receptionist: "Great. We have openings this week — would a morning or afternoon appointment work better for you? Dr. [Name] has availability on [Day] at [Time]. Does that work?"

Caller: "That sounds great."

Receptionist: "Excellent! I'll send you our new-patient forms by email so you can fill them out before your visit. We look forward to seeing you on [Day]. Have a wonderful day!"`,
  },
  {
    id: "appointment-scheduling",
    title: "Appointment Scheduling",
    description:
      "Standard script for scheduling, rescheduling, or confirming appointments.",
    icon: "2",
    body: `Receptionist: "Thank you for calling [Practice Name], this is [Your Name]. How can I help you?"

Caller: "I need to schedule a cleaning."

Receptionist: "I'd be happy to schedule that for you. May I have your name so I can pull up your account?"

Caller: "[Name]"

Receptionist: "Thank you, [Name]. I see you're due for your six-month cleaning. Do you have a preference for morning or afternoon?"

Caller: "Morning works best."

Receptionist: "Dr. [Name] has availability on Tuesday at 9:00 AM or Thursday at 10:30 AM. Which would you prefer?"

Caller: "Tuesday at 9 works."

Receptionist: "Perfect, I've got you down for Tuesday at 9:00 AM with Dr. [Name] for a routine cleaning and exam. We'll send you a reminder text the day before. Is there anything else I can help you with?"

Caller: "No, that's it."

Receptionist: "Great! We'll see you Tuesday morning. Have a good day!"`,
  },
  {
    id: "insurance-inquiry",
    title: "Insurance Inquiry",
    description:
      "Handle insurance verification questions with confidence and accuracy.",
    icon: "3",
    body: `Receptionist: "Thank you for calling [Practice Name], this is [Your Name]. How can I help you today?"

Caller: "Hi, I wanted to check if you accept my insurance."

Receptionist: "Absolutely, I can help with that. We accept most major dental insurance plans. Could I get your insurance provider name and your member ID number?"

Caller: "It's [Insurance Company], member ID [Number]."

Receptionist: "Thank you. Let me verify your coverage. This usually takes a moment. [Pause] I can see that your plan covers preventive services like cleanings and exams at 100%, and major procedures at 50% after your deductible."

Receptionist: "Keep in mind that benefits can vary, so we always run a full verification before your appointment to make sure there are no surprises. Would you like to schedule a visit?"

Caller: "Yes, that would be great."

Receptionist: "Wonderful. I'll note your insurance information on file. What day works best for you? We have openings this week and next."

Caller: [Responds]

Receptionist: "Perfect, you're all set. We'll confirm your insurance details before your visit so everything goes smoothly. See you soon!"`,
  },
  {
    id: "after-hours-voicemail",
    title: "After-Hours Voicemail",
    description:
      "Professional voicemail greeting for nights, weekends, and holidays.",
    icon: "4",
    body: `"Thank you for calling [Practice Name]. Our office is currently closed.

Our regular office hours are Monday through Friday, 8:00 AM to 5:00 PM, and Saturday from 9:00 AM to 1:00 PM.

If you are experiencing a dental emergency — such as severe pain, swelling, or a knocked-out tooth — please call our emergency line at [Emergency Number]. A member of our team will return your call as soon as possible.

For all other inquiries, please leave your name, phone number, and a brief message after the tone, and we will return your call on the next business day.

You can also visit our website at [Website URL] to request an appointment online, check our services, or access patient forms.

If you'd like to schedule or confirm an appointment, feel free to leave those details and we'll get back to you first thing in the morning.

Thank you for choosing [Practice Name]. We look forward to speaking with you soon."`,
  },
  {
    id: "follow-up-call",
    title: "Follow-Up Call",
    description:
      "Post-procedure check-in call to build patient loyalty and catch issues early.",
    icon: "5",
    body: `Receptionist: "Hi [Patient Name], this is [Your Name] from [Practice Name]. I'm calling to check in after your [procedure — e.g., filling / extraction / crown placement] yesterday. How are you feeling today?"

Patient: [Responds]

Receptionist: "That's perfectly normal. [If mild discomfort:] Some tenderness is expected for the first day or two. You can take over-the-counter ibuprofen as directed, and try to stick to softer foods for the next 24 hours."

Receptionist: "If you notice any increased swelling, bleeding that doesn't stop, or pain that gets worse rather than better, please give us a call right away so we can take a look."

Patient: "Okay, thank you."

Receptionist: "Of course! Dr. [Name] wanted me to make sure you're doing well — your comfort is our priority. Do you have any other questions about your care instructions?"

Patient: "No, I think I'm good."

Receptionist: "Wonderful. Remember, you're scheduled for your follow-up on [Date] at [Time]. We'll send you a reminder the day before. If anything comes up before then, don't hesitate to call. Have a great rest of your day, [Patient Name]!"`,
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function DownloadForm() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!email || !consent) return;
      // Track conversion (no API call — just local unlock)
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "lead", {
          event_category: "resource_download",
          event_label: "dental_scripts",
          value: email,
        });
      }
      setUnlocked(true);
    },
    [email, consent],
  );

  const copyScript = useCallback(async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) { /* clipboard not available */ console.error("[DownloadForm]:", err); }
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className="border-b border-gray-200 dark:border-gray-800 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-2 font-bold text-lg"
          >
            <span className="w-7 h-7 bg-gradient-to-br from-blue-600 to-purple-600 rounded flex items-center justify-center text-white text-xs font-bold">
              AL
            </span>
            Aditya Labs
          </Link>
          <div className="flex items-center gap-6 text-sm">
            <Link
              href="/pricing"
              className="hover:text-blue-600 dark:hover:text-blue-400"
            >
              Pricing
            </Link>
            <Link
              href="/signup"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Sign Up
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <header className="py-16 md:py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full mb-4">
            FREE RESOURCE
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Free Dental Reception Script&nbsp;Pack
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            5 professional phone scripts to train your front desk team and never
            lose a patient call again.
          </p>
        </div>
      </header>

      {/* ── What's Included ─────────────────────────────────────────── */}
      <section className="px-6 pb-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-10">
            What&apos;s Included
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SCRIPTS.map((s) => (
              <div
                key={s.id}
                className="border border-gray-200 dark:border-gray-800 rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center font-bold text-lg mb-4">
                  {s.icon}
                </div>
                <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {s.description}
                </p>
              </div>
            ))}
            {/* Bonus card */}
            <div className="border border-dashed border-blue-400 dark:border-blue-600 rounded-xl p-6 bg-blue-50/50 dark:bg-blue-900/10 flex flex-col items-center justify-center text-center">
              <span className="text-3xl mb-2">+</span>
              <h3 className="font-semibold text-lg mb-1">
                Customisation Tips
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Each script includes bracket placeholders — just swap in your
                practice details and you&apos;re ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Email Gate / Scripts ────────────────────────────────────── */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          {!unlocked ? (
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-2xl font-bold mb-2">
                Get Your Free Script Pack
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                Enter your email below and all 5 scripts will appear instantly
                &mdash; ready to copy and customise.
              </p>
              <form
                onSubmit={handleSubmit}
                className="max-w-md mx-auto space-y-4"
              >
                <input
                  type="email"
                  required
                  placeholder="you@yourpractice.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <label className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400 text-left cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 accent-blue-600"
                  />
                  <span>
                    I agree to receive occasional tips from Aditya Labs. You can
                    unsubscribe anytime.
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={!email || !consent}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Show Me the Scripts
                </button>
              </form>
            </div>
          ) : (
            <div className="space-y-10">
              <div className="text-center mb-6">
                <span className="inline-block bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm font-semibold px-4 py-1.5 rounded-full">
                  Unlocked &mdash; All 5 Scripts
                </span>
              </div>

              {SCRIPTS.map((s) => (
                <div
                  key={s.id}
                  className="border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden"
                >
                  <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex items-center justify-between">
                    <h3 className="font-semibold text-lg">
                      <span className="text-blue-600 dark:text-blue-400 mr-2">
                        #{s.icon}
                      </span>
                      {s.title}
                    </h3>
                    <button
                      onClick={() => copyScript(s.id, s.body)}
                      className="text-sm font-medium px-4 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      {copied === s.id ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <pre className="px-6 py-5 text-sm leading-relaxed whitespace-pre-wrap font-sans text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-950">
                    {s.body}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-10 md:p-14 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Want an AI That Reads These Scripts&nbsp;24/7?
          </h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Our AI receptionist answers calls, books appointments, and handles
            insurance questions using scripts just like these &mdash;
            automatically.
          </p>
          <Link
            href="/tools/script-generator"
            className="inline-block bg-white text-blue-700 font-semibold px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Try the AI Version &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
